import { Typography } from '@material-ui/core';
import { FormikPasswordField } from '@superdispatch/forms';
import { Inline, Stack, useResponsiveValue, useUID } from '@superdispatch/ui';
import { Alert, Button } from '@superdispatch/ui-lab';
import { Form, FormikProvider } from 'formik';
import { useNavigate } from 'react-router-dom';
import { LoginLayoutContent } from 'shared/auth/core/LoginLayoutContent';
import {
  AuthResponse,
  useAuthAPI,
  useResetPasswordCode,
} from 'shared/auth/data/AuthAPI';
import { confirmPasswordResetSchema } from 'shared/auth/data/AuthDTO';
import { trackLoginEvent } from 'shared/auth/data/LoginAnalytics';
import { ForgotPasswordPage } from 'shared/auth/ForgotPasswordPage';
import { useAppFormik } from 'shared/form/AppFormik';
import { useSearchParam } from 'shared/helpers/SearchParamsHelpers';
import { SplashScreen } from 'shared/layout/SplashScreen';
import { useRedirectToTMSLanding } from 'shared/routing/TMSLandingPath';
import { yupString } from 'shared/utils/YupUtils';
import { ref } from 'yup';
import { updateAuthTokenLocally } from './AuthToken';
import { PasswordStrength } from '@superdispatch/ui-lab';

const formSchema = confirmPasswordResetSchema.shape({
  password_confirm: yupString().oneOf(
    [ref('password')],
    'Passwords do not match',
  ),
});

export interface ResetPasswordPage {
  onOpenLogin: () => void;
}

export function ResetPasswordPage({ onOpenLogin }: ResetPasswordPage) {
  const uid = useUID();
  const navigate = useNavigate();
  const { confirmPasswordReset } = useAuthAPI();
  const [resetCode = ''] = useSearchParam('reset_code');
  const { error, isInitialLoading } = useResetPasswordCode(resetCode);
  const isMobile = useResponsiveValue(true, false);
  const { redirectToTMSLanding } = useRedirectToTMSLanding();

  const formik = useAppFormik({
    validationSchema: formSchema,
    onSubmit(values) {
      return confirmPasswordReset(resetCode, values.password);
    },
    onSubmitSuccess(response: AuthResponse) {
      if (isMobile || !response.data.has_dispatcher_role) {
        navigate(
          { pathname: `/reset-password-success` },
          {
            state: {
              has_dispatcher_role: response.data.has_dispatcher_role,
              token: response.data.token,
            },
          },
        );
      } else {
        updateAuthTokenLocally(response.data.token);
        redirectToTMSLanding();
      }
      trackLoginEvent({ name: 'CTMS: Reset Password Success via Email' });
    },
    getErrorMessage({ message }) {
      return message || 'Error occurred, contact support.';
    },
  });

  if (isInitialLoading) return <SplashScreen />;

  if (error) {
    return (
      <ForgotPasswordPage
        onOpenLogin={onOpenLogin}
        isResetLinkExpiredPage={true}
      />
    );
  }

  return (
    <LoginLayoutContent>
      <FormikProvider value={formik}>
        <Form aria-labelledby={uid}>
          <Stack space="large">
            <Stack space="small">
              <Inline space="xsmall" verticalAlign="center">
                <Typography id={uid} variant="h2">
                  Reset Password
                </Typography>
              </Inline>

              <Alert severity="caution">
                Updating your password will automatically apply to both your
                Dispatcher and Driver accounts if you use the same email
              </Alert>
            </Stack>

            <Stack space="small">
              <Stack space="xsmall">
                <FormikPasswordField
                  name="password"
                  label="New Password"
                  fullWidth={true}
                />

                <PasswordStrength value={formik.values.password} />
              </Stack>

              <FormikPasswordField
                name="password_confirm"
                label="Re-enter Your New Password"
                fullWidth={true}
              />
            </Stack>

            <Button
              type="submit"
              size="large"
              fullWidth={true}
              pending={formik.isSubmitting}
              disabled={!formik.isValid || !formik.dirty}
              onClick={() => {
                trackLoginEvent({ name: 'CTMS: Clicked Reset Password' });
              }}
            >
              Reset Password
            </Button>
          </Stack>
        </Form>
      </FormikProvider>
    </LoginLayoutContent>
  );
}
